import { computeTapeoutCommitment, encodeNetlist, netlistHash } from "@tapeout/netlist";
import {
  PaymentKind,
  ProjectState,
  TAPEOUT_FEE,
  paymentTokenAbi,
  projectCoreAbi,
  projectEventAbi,
  tapeoutProjectCoreAbi,
  tapeoutRevealedFromReceipt,
} from "@tapeout/sdk";
import {
  BaseError,
  bytesToHex,
  createPublicClient,
  createWalletClient,
  defineChain,
  getAddress,
  http,
  isAddress,
  parseAbi,
  parseAbiItem,
  parseEventLogs,
  zeroAddress,
  type Address,
  type Hex,
  type TransactionReceipt,
} from "viem";
import type { PrivateKeyAccount } from "viem/accounts";

import type { ProjectSnapshot, RuntimeConfig, TapeoutSecret, WalletStage } from "./batchTypes.js";

const identityAbi = parseAbi([
  "function tokenName() view returns (string)",
  "function tokenSymbol() view returns (string)",
]);

const nandOperationsAbi = parseAbi([
  "function balanceOf(address owner) view returns (uint256)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function consumed(uint256 tokenId) view returns (bool)",
  "function refunded(uint256 tokenId) view returns (bool)",
]);

const erc721BalanceAbi = parseAbi(["function balanceOf(address owner) view returns (uint256)"]);
const transferEvent = parseAbiItem(
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)",
);

interface NandTransferLog {
  readonly args: {
    readonly from?: Address | undefined;
    readonly to?: Address | undefined;
    readonly tokenId?: bigint | undefined;
  };
}

const DEFAULT_NETLIST = encodeNetlist([
  { lhs: 2, rhs: 3 },
  { lhs: 4, rhs: 4 },
  { lhs: 5, rhs: 5 },
  { lhs: 6, rhs: 6 },
  { lhs: 7, rhs: 7 },
]);

export interface StageUpdate {
  readonly stage: WalletStage;
  readonly detail: string;
  readonly hash?: Hex;
}

export interface MintResult {
  readonly ids: readonly bigint[];
  readonly hash: Hex;
  readonly approvalHash?: Hex;
}

export interface TapeoutResult {
  readonly minerId: bigint;
  readonly commitHash: Hex;
  readonly revealHash: Hex;
}

export interface BatchRuntime {
  readonly config: RuntimeConfig;
  readonly chain: ReturnType<typeof defineChain>;
  readonly publicClient: ReturnType<typeof createPublicClient>;
}

export interface WalletAssetSnapshot {
  readonly address: Address;
  readonly paymentBalance: bigint;
  readonly nandBalance: bigint;
  readonly minerBalance: bigint;
}

function assertRpcUrl(value: string): void {
  const url = new URL(value);
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("RPC 地址必须使用 http 或 https");
  }
}

export function createRuntime(config: RuntimeConfig): BatchRuntime {
  assertRpcUrl(config.rpcUrl);
  const chain = defineChain({
    id: config.chainId,
    name: `Chain ${config.chainId}`,
    nativeCurrency: {
      name: config.nativeSymbol,
      symbol: config.nativeSymbol,
      decimals: 18,
    },
    rpcUrls: { default: { http: [config.rpcUrl] } },
  });
  return {
    config,
    chain,
    publicClient: createPublicClient({
      chain,
      transport: http(config.rpcUrl),
      // Keep mainnet receipt polling conservative for rate-limited RPCs. The
      // ephemeral Hardhat fixture mines every second and can be polled faster.
      pollingInterval: config.chainId === 31_337 ? 100 : 4_000,
    }),
  };
}

export async function inspectProject(config: RuntimeConfig): Promise<ProjectSnapshot> {
  if (!isAddress(config.projectAddress)) throw new Error("项目地址无效");
  const runtime = createRuntime(config);
  const actualChainId = await runtime.publicClient.getChainId();
  if (actualChainId !== config.chainId) {
    throw new Error(`RPC 实际链 ID 为 ${actualChainId}，与配置的 ${config.chainId} 不一致`);
  }
  const bytecode = await runtime.publicClient.getCode({ address: config.projectAddress });
  if (bytecode === undefined || bytecode === "0x") throw new Error("项目地址没有合约代码");

  const [
    tokenName,
    tokenSymbol,
    state,
    nandPrice,
    nandCap,
    sold,
    paymentKind,
    paymentToken,
    nandNft,
    minerNft,
  ] = await Promise.all([
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: identityAbi,
      functionName: "tokenName",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: identityAbi,
      functionName: "tokenSymbol",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: projectCoreAbi,
      functionName: "state",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: projectCoreAbi,
      functionName: "nandPriceWei",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: projectCoreAbi,
      functionName: "nandCap",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: projectCoreAbi,
      functionName: "sold",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: projectCoreAbi,
      functionName: "paymentKind",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: projectCoreAbi,
      functionName: "paymentToken",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: projectCoreAbi,
      functionName: "nandNFT",
    }),
    runtime.publicClient.readContract({
      address: config.projectAddress,
      abi: projectCoreAbi,
      functionName: "minerNFT",
    }),
  ]);

  const payment = Number(paymentKind);
  if (payment !== PaymentKind.NATIVE && payment !== PaymentKind.ERC20) {
    throw new Error("项目支付资产类型不受支持");
  }
  let paymentSymbol = config.nativeSymbol;
  let paymentDecimals = 18;
  if (payment === PaymentKind.ERC20) {
    if (paymentToken === zeroAddress) throw new Error("项目 ERC-20 支付代币地址无效");
    [paymentSymbol, paymentDecimals] = await Promise.all([
      runtime.publicClient.readContract({
        address: paymentToken,
        abi: paymentTokenAbi,
        functionName: "symbol",
      }),
      runtime.publicClient.readContract({
        address: paymentToken,
        abi: paymentTokenAbi,
        functionName: "decimals",
      }),
    ]);
  }

  return {
    address: getAddress(config.projectAddress),
    tokenName,
    tokenSymbol,
    state: Number(state),
    nandPrice,
    nandCap,
    sold,
    paymentKind: payment,
    paymentToken,
    paymentSymbol,
    paymentDecimals: Number(paymentDecimals),
    nandNft,
    minerNft,
    actualChainId,
  };
}

export async function inspectWalletAssets(
  runtime: BatchRuntime,
  snapshot: ProjectSnapshot,
  addresses: readonly Address[],
  onResult?: (result: WalletAssetSnapshot) => void,
): Promise<readonly WalletAssetSnapshot[]> {
  const results: WalletAssetSnapshot[] = [];
  for (const [index, address] of addresses.entries()) {
    const paymentBalancePromise =
      snapshot.paymentKind === PaymentKind.NATIVE
        ? runtime.publicClient.getBalance({ address })
        : runtime.publicClient.readContract({
            address: snapshot.paymentToken,
            abi: paymentTokenAbi,
            functionName: "balanceOf",
            args: [address],
          });
    const [paymentBalance, nandBalance, minerBalance] = await Promise.all([
      paymentBalancePromise,
      runtime.publicClient.readContract({
        address: snapshot.nandNft,
        abi: erc721BalanceAbi,
        functionName: "balanceOf",
        args: [address],
      }),
      runtime.publicClient.readContract({
        address: snapshot.minerNft,
        abi: erc721BalanceAbi,
        functionName: "balanceOf",
        args: [address],
      }),
    ]);
    const result = { address, paymentBalance, nandBalance, minerBalance };
    results.push(result);
    onResult?.(result);
    if (index + 1 < addresses.length) {
      await new Promise((resolve) => globalThis.setTimeout(resolve, 200));
    }
  }
  return results;
}

async function readTransferLogs(
  runtime: BatchRuntime,
  snapshot: ProjectSnapshot,
  fromBlock: bigint,
  toBlock: bigint,
): Promise<readonly NandTransferLog[]> {
  try {
    return await runtime.publicClient.getLogs({
      address: snapshot.nandNft,
      event: transferEvent,
      fromBlock,
      toBlock,
    });
  } catch (reason) {
    if (fromBlock === toBlock) throw reason;
    const middle = (fromBlock + toBlock) / 2n;
    const left = await readTransferLogs(runtime, snapshot, fromBlock, middle);
    await new Promise((resolve) => globalThis.setTimeout(resolve, 100));
    const right = await readTransferLogs(runtime, snapshot, middle + 1n, toBlock);
    return [...left, ...right];
  }
}

export async function inspectNandOwnership(
  runtime: BatchRuntime,
  snapshot: ProjectSnapshot,
): Promise<ReadonlyMap<string, readonly bigint[]>> {
  const ownerByToken = new Map<bigint, Address>();
  const latestBlock = await runtime.publicClient.getBlockNumber();
  const fromBlock = runtime.config.scanFromBlock ?? 0n;
  if (fromBlock <= latestBlock) {
    const blockRange = 20_000n;
    for (let start = fromBlock; start <= latestBlock; start += blockRange) {
      const end = start + blockRange - 1n > latestBlock ? latestBlock : start + blockRange - 1n;
      const logs = await readTransferLogs(runtime, snapshot, start, end);
      for (const log of logs) {
        const tokenId = log.args.tokenId;
        const receiver = log.args.to;
        if (tokenId === undefined) continue;
        if (receiver === undefined || receiver === zeroAddress) ownerByToken.delete(tokenId);
        else ownerByToken.set(tokenId, receiver);
      }
      if (end < latestBlock) {
        await new Promise((resolve) => globalThis.setTimeout(resolve, 100));
      }
    }
  }

  const owned = new Map<string, bigint[]>();
  for (const [tokenId, owner] of ownerByToken) {
    const key = owner.toLowerCase();
    const ids = owned.get(key) ?? [];
    ids.push(tokenId);
    owned.set(key, ids);
  }
  for (const ids of owned.values()) ids.sort((left, right) => (left < right ? -1 : 1));
  return owned;
}

function walletFor(runtime: BatchRuntime, account: PrivateKeyAccount) {
  return createWalletClient({
    account,
    chain: runtime.chain,
    transport: http(runtime.config.rpcUrl),
  });
}

function requireSuccess(receipt: TransactionReceipt, label: string): void {
  if (receipt.status !== "success") throw new Error(`${label}交易执行失败`);
}

function purchasedIds(receipt: TransactionReceipt, buyer: Address): readonly bigint[] {
  const events = parseEventLogs({
    abi: projectEventAbi,
    eventName: "NandPurchased",
    logs: receipt.logs,
    strict: true,
  });
  const event = events.find(
    (candidate) => candidate.args.buyer.toLowerCase() === buyer.toLowerCase(),
  );
  if (event === undefined) throw new Error("购买已确认，但未找到 NAND 购买事件");
  const ids: bigint[] = [];
  for (let id = event.args.firstTokenId; id <= event.args.lastTokenId; id += 1n) ids.push(id);
  return ids;
}

export async function mintNands(input: {
  readonly runtime: BatchRuntime;
  readonly snapshot: ProjectSnapshot;
  readonly account: PrivateKeyAccount;
  readonly quantity: bigint;
  readonly approvalAmount?: bigint;
  readonly onUpdate: (update: StageUpdate) => void;
}): Promise<MintResult> {
  const { runtime, snapshot, account, quantity, onUpdate } = input;
  if (snapshot.state !== ProjectState.FUNDRAISING) throw new Error("项目当前不在募资阶段");
  const wallet = walletFor(runtime, account);
  const amount = snapshot.nandPrice * quantity;
  const approvalAmount = input.approvalAmount ?? amount;
  if (approvalAmount < amount) throw new Error("授权金额不能小于本次 Mint 金额");
  let approvalHash: Hex | undefined;

  onUpdate({ stage: "checking", detail: "校验余额、额度与项目余量" });
  const freshSold = await runtime.publicClient.readContract({
    address: snapshot.address,
    abi: projectCoreAbi,
    functionName: "sold",
  });
  if (freshSold + quantity > snapshot.nandCap) throw new Error("项目剩余 NAND 不足");

  if (snapshot.paymentKind === PaymentKind.ERC20) {
    const [balance, allowance] = await Promise.all([
      runtime.publicClient.readContract({
        address: snapshot.paymentToken,
        abi: paymentTokenAbi,
        functionName: "balanceOf",
        args: [account.address],
      }),
      runtime.publicClient.readContract({
        address: snapshot.paymentToken,
        abi: paymentTokenAbi,
        functionName: "allowance",
        args: [account.address, snapshot.address],
      }),
    ]);
    if (balance < amount) throw new Error(`${snapshot.paymentSymbol} 余额不足`);
    if (allowance < amount) {
      onUpdate({ stage: "approving", detail: `授权 ${snapshot.paymentSymbol}` });
      const approval = await runtime.publicClient.simulateContract({
        account,
        address: snapshot.paymentToken,
        abi: paymentTokenAbi,
        functionName: "approve",
        args: [snapshot.address, approvalAmount],
      });
      approvalHash = await wallet.writeContract(approval.request);
      onUpdate({ stage: "approving", detail: "等待授权确认", hash: approvalHash });
      requireSuccess(
        await runtime.publicClient.waitForTransactionReceipt({ hash: approvalHash }),
        "授权",
      );
    }
  } else {
    const balance = await runtime.publicClient.getBalance({ address: account.address });
    if (balance < amount) throw new Error(`${runtime.config.nativeSymbol} 余额不足`);
  }

  onUpdate({ stage: "minting", detail: `购买 ${quantity.toString()} 枚 NAND` });
  const simulation = await runtime.publicClient.simulateContract({
    account,
    address: snapshot.address,
    abi: projectCoreAbi,
    functionName: "buyNand",
    args: [quantity, account.address],
    value: snapshot.paymentKind === PaymentKind.NATIVE ? amount : 0n,
  });
  const hash = await wallet.writeContract(simulation.request);
  onUpdate({ stage: "minting", detail: "等待 Mint 确认", hash });
  const receipt = await runtime.publicClient.waitForTransactionReceipt({ hash });
  requireSuccess(receipt, "Mint");
  return {
    ids: purchasedIds(receipt, account.address),
    hash,
    ...(approvalHash === undefined ? {} : { approvalHash }),
  };
}

function randomHex32(): Hex {
  return bytesToHex(globalThis.crypto.getRandomValues(new Uint8Array(32)));
}

async function validateNandGroup(
  runtime: BatchRuntime,
  snapshot: ProjectSnapshot,
  owner: Address,
  ids: readonly [bigint, bigint, bigint, bigint, bigint],
): Promise<void> {
  if (new Set(ids).size !== 5 || ids.some((id) => id <= 0n))
    throw new Error("NAND ID 必须是 5 个不同的正整数");
  const blockNumber = await runtime.publicClient.getBlockNumber();
  const results = await Promise.all(
    ids.map(async (id) => {
      const [actualOwner, consumed, refunded] = await Promise.all([
        runtime.publicClient.readContract({
          address: snapshot.nandNft,
          abi: nandOperationsAbi,
          functionName: "ownerOf",
          args: [id],
          blockNumber,
        }),
        runtime.publicClient.readContract({
          address: snapshot.nandNft,
          abi: nandOperationsAbi,
          functionName: "consumed",
          args: [id],
          blockNumber,
        }),
        runtime.publicClient.readContract({
          address: snapshot.nandNft,
          abi: nandOperationsAbi,
          functionName: "refunded",
          args: [id],
          blockNumber,
        }),
      ]);
      return { actualOwner, consumed, refunded };
    }),
  );
  if (
    results.some(
      ({ actualOwner, consumed, refunded }) =>
        actualOwner.toLowerCase() !== owner.toLowerCase() || consumed || refunded,
    )
  ) {
    throw new Error("NAND 所有权或可用状态校验失败");
  }
}

async function waitUntilRevealable(runtime: BatchRuntime, commitBlock: bigint): Promise<void> {
  let current = await runtime.publicClient.getBlockNumber();
  if (current > commitBlock) return;
  if (runtime.config.chainId === 31337) {
    try {
      await runtime.publicClient.request({ method: "evm_mine", params: [] });
      current = await runtime.publicClient.getBlockNumber();
    } catch {
      // A local RPC may disable development methods; fall through to polling.
    }
  }
  const deadline = Date.now() + 120_000;
  while (current <= commitBlock && Date.now() < deadline) {
    await new Promise((resolve) => globalThis.setTimeout(resolve, 1_000));
    current = await runtime.publicClient.getBlockNumber();
  }
  if (current <= commitBlock) throw new Error("等待新区块超时；commit 已保留，可稍后继续 reveal");
}

export async function runTapeout(input: {
  readonly runtime: BatchRuntime;
  readonly snapshot: ProjectSnapshot;
  readonly account: PrivateKeyAccount;
  readonly nandIds: readonly bigint[];
  readonly existingSecret?: TapeoutSecret;
  readonly onSecret: (secret: TapeoutSecret) => void;
  readonly onUpdate: (update: StageUpdate) => void;
}): Promise<TapeoutResult> {
  const { runtime, snapshot, account, onSecret, onUpdate } = input;
  if (snapshot.state === ProjectState.FAILED) throw new Error("失败项目不能流片");
  const sorted = [...input.nandIds].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  if (sorted.length !== 5) throw new Error("每次流片必须指定 5 枚 NAND");
  const ids = sorted as [bigint, bigint, bigint, bigint, bigint];
  await validateNandGroup(runtime, snapshot, account.address, ids);
  const wallet = walletFor(runtime, account);
  let secret = input.existingSecret;

  if (secret === undefined) {
    const userNonce = randomHex32();
    const salt = randomHex32();
    secret = {
      nandIds: ids,
      receiver: account.address,
      netlist: DEFAULT_NETLIST,
      userNonce,
      salt,
      commitment: computeTapeoutCommitment({
        chainId: BigInt(runtime.config.chainId),
        projectCore: snapshot.address,
        designer: account.address,
        receiver: account.address,
        sortedNandIds: ids,
        netlistHash: netlistHash(DEFAULT_NETLIST),
        userNonce,
        salt,
      }),
    };
    onSecret(secret);
  }

  let commitHash = secret.commitHash;
  let commitBlock = secret.commitBlock;
  let deadlineBlock = secret.deadlineBlock;
  if (commitHash === undefined || commitBlock === undefined || deadlineBlock === undefined) {
    onUpdate({ stage: "committing", detail: "提交流片承诺" });
    const commitSimulation = await runtime.publicClient.simulateContract({
      account,
      address: snapshot.address,
      abi: tapeoutProjectCoreAbi,
      functionName: "commitTapeout",
      args: [secret.commitment],
    });
    commitHash = await wallet.writeContract(commitSimulation.request);
    onUpdate({ stage: "committing", detail: "等待 commit 确认", hash: commitHash });
    const commitReceipt = await runtime.publicClient.waitForTransactionReceipt({
      hash: commitHash,
    });
    requireSuccess(commitReceipt, "Commit");
    [commitBlock, deadlineBlock] = await runtime.publicClient.readContract({
      address: snapshot.address,
      abi: tapeoutProjectCoreAbi,
      functionName: "tapeoutCommit",
      args: [account.address, secret.commitment],
    });
    if (commitBlock === 0n) throw new Error("commit 已确认，但链上承诺记录不存在");
    secret = { ...secret, commitHash, commitBlock, deadlineBlock };
    onSecret(secret);
  }

  onUpdate({ stage: "waiting", detail: `等待区块 ${String(commitBlock + 1n)}`, hash: commitHash });
  await waitUntilRevealable(runtime, commitBlock);
  const latest = await runtime.publicClient.getBlockNumber();
  if (latest > deadlineBlock) throw new Error("commit 已过期，不能 reveal");
  await validateNandGroup(runtime, snapshot, account.address, ids);

  const nativeBalance = await runtime.publicClient.getBalance({ address: account.address });
  if (nativeBalance < TAPEOUT_FEE) {
    throw new Error(`${runtime.config.nativeSymbol} 余额不足，无法支付流片费`);
  }
  onUpdate({ stage: "revealing", detail: "公开设计并铸造矿机", hash: commitHash });
  const revealSimulation = await runtime.publicClient.simulateContract({
    account,
    address: snapshot.address,
    abi: tapeoutProjectCoreAbi,
    functionName: "revealTapeout",
    args: [secret.receiver, secret.nandIds, secret.netlist, secret.userNonce, secret.salt],
    value: TAPEOUT_FEE,
  });
  const revealHash = await wallet.writeContract(revealSimulation.request);
  onUpdate({ stage: "revealing", detail: "等待 reveal 确认", hash: revealHash });
  const revealReceipt = await runtime.publicClient.waitForTransactionReceipt({ hash: revealHash });
  requireSuccess(revealReceipt, "Reveal");
  const event = tapeoutRevealedFromReceipt({
    ...revealReceipt,
    logs: revealReceipt.logs.filter(
      (log) => log.address.toLowerCase() === snapshot.address.toLowerCase(),
    ),
  });
  if (event === undefined || event.designer.toLowerCase() !== account.address.toLowerCase()) {
    throw new Error("流片已确认，但未找到匹配的矿机事件");
  }
  return { minerId: event.minerId, commitHash, revealHash };
}

export function sanitizeChainError(reason: unknown): string {
  const raw =
    reason instanceof BaseError
      ? reason.shortMessage
      : reason instanceof Error
        ? reason.message
        : "未知链上错误";
  return raw
    .replace(/0x[0-9a-fA-F]{64}/g, "[已隐藏 32 字节数据]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 280);
}
